import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import axios from 'axios';
import PlayerLayout from '../messages/player/PlayerLayout';
import PlayerNav from '../messages/player/PlayerNav';
import url from '../constants/config';
import Loader from '../shared/displayComponents/loader/Loader';
import {
  fetchMyChecklist,
  uploadMyAttachment,
  deleteMyAttachment,
  addMyLink,
  deleteMyLink,
  markMyTaskRead,
} from './store/checklistSlice';
import TaskCommentPanel from './TaskCommentPanel';
import CommunicationLog from './CommunicationLog';
import FileUploadModal from './FileUploadModal';
import ChecklistLinkDate from './ChecklistLinkDate';

const STEP_TITLES = {
  1: 'START REKRUTACJI',
  2: 'PO AKCEPTACJI UCZELNI',
  3: 'PRZYGOTOWANIE DO WYLOTU',
};

const STATUS_BADGE = {
  not_started:        { label: 'Do rozpoczęcia',           color: '#64748b', bg: '#f1f5f9' },
  in_progress:        { label: 'W realizacji',             color: '#92400e', bg: '#fef3c7' },
  waiting_athlete:    { label: 'Oczekuje na sportowca',    color: '#1e40af', bg: '#dbeafe' },
  waiting_university: { label: 'Oczekuje na uczelnię',     color: '#6d28d9', bg: '#ede9fe' },
  waiting_playsport:  { label: 'Oczekuje na PLAYSPORTUSA', color: '#0f766e', bg: '#ccfbf1' },
  to_verify:          { label: 'Do weryfikacji',           color: '#c2410c', bg: '#ffedd5' },
  on_hold:            { label: 'Wstrzymane',               color: '#475569', bg: '#e2e8f0' },
  done:               { label: 'Zrealizowane \u2713',      color: '#15803d', bg: '#dcfce7' },
  // backward compat with existing data
  waiting:            { label: 'Oczekiwanie',              color: '#1e40af', bg: '#dbeafe' },
};

const getAttachmentDownloadUrl = (taskId, attachmentId) =>
  `${url}/checklist/me/tasks/${taskId}/attachments/${attachmentId}`;

const openAttachment = async (fileUrl, filename) => {
  try {
    const token = window.localStorage.getItem('authorization');
    const response = await fetch(fileUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch attachment');
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const newTab = window.open(blobUrl, '_blank');
    if (!newTab) {
      // Popup blocked — fall back to download
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to open attachment:', err);
  }
};

const formatDeadline = (deadline) => {
  if (!deadline) return null;
  return new Date(deadline).toLocaleDateString('pl-PL', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const displayLinkHost = (href) => {
  try {
    const u = new URL(href);
    return (u.hostname + (u.pathname.length > 1 ? u.pathname : '')).slice(0, 50);
  } catch {
    return href.slice(0, 50);
  }
};


const FILTER_STATUS_OPTIONS = [
  { val: null,                lbl: 'Wszystkie' },
  { val: 'not_started',       lbl: 'Do rozp.' },
  { val: 'in_progress',       lbl: 'W realizacji' },
  { val: 'waiting_athlete',   lbl: 'Oczek. sportowiec' },
  { val: 'waiting_university',lbl: 'Oczek. uczelnia' },
  { val: 'waiting_playsport', lbl: 'Oczek. PSA' },
  { val: 'to_verify',         lbl: 'Do weryfikacji' },
  { val: 'on_hold',           lbl: 'Wstrzymane' },
  { val: 'done',              lbl: 'Zrealizowane' },
];

const PIPELINE_STAGES = [
  { key: 'sport_recruitment', label: 'Rekrutacja sportowa', icon: '🔎' },
  { key: 'after_offer', label: 'Po akceptacji oferty', icon: '🎓' },
  { key: 'finalization', label: 'Finalizacja procesu', icon: '✈️' },
];

const RecruitmentProgressBar = ({ stage }) => {
  const currentIdx = PIPELINE_STAGES.findIndex(s => s.key === stage);
  const activeIdx = currentIdx === -1 ? 0 : currentIdx;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        padding: '16px 20px',
        marginBottom: '20px',
      }}
    >
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' }}>
        Etap rekrutacji
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
        {PIPELINE_STAGES.map((s, idx) => {
          const isActive = idx === activeIdx;
          const isDone = idx < activeIdx;
          return (
            <React.Fragment key={s.key}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: isActive ? '#2e496b' : isDone ? '#16a34a' : '#e2e8f0',
                    color: isActive || isDone ? '#fff' : '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isActive || isDone ? '16px' : '14px',
                    fontWeight: 700,
                    border: isActive ? '3px solid #93c5fd' : 'none',
                    flexShrink: 0,
                    transition: 'all 0.2s',
                  }}
                >
                  {isDone ? '✓' : s.icon}
                </div>
                <div
                  style={{
                    marginTop: '6px',
                    fontSize: '11px',
                    fontWeight: isActive ? 700 : 400,
                    color: isActive ? '#1e293b' : isDone ? '#16a34a' : '#94a3b8',
                    textAlign: 'center',
                    lineHeight: 1.2,
                    maxWidth: '72px',
                    wordBreak: 'break-word',
                  }}
                >
                  {s.label}
                </div>
              </div>
              {idx < PIPELINE_STAGES.length - 1 && (
                <div
                  style={{
                    flex: 0,
                    width: '100%',
                    maxWidth: '32px',
                    height: '3px',
                    background: idx < activeIdx ? '#16a34a' : '#e2e8f0',
                    borderRadius: '2px',
                    marginBottom: '16px',
                    transition: 'background 0.2s',
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const PlayerChecklist = () => {
  const dispatch = useDispatch();
  const { items, loading, step2Unlocked, step3Unlocked } = useSelector(({ checklist }) => checklist);
  const userRole = useSelector(({ authorization }) => authorization.userRole);
  const location = useLocation();
  const history = useHistory();
  const [newLinkInputs, setNewLinkInputs] = useState({});
  const [filterStatus, setFilterStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('tasks');
  const [selectedTask, setSelectedTask] = useState(null);
  const [uploadModalTaskId, setUploadModalTaskId] = useState(null);
  const [recruitmentStage, setRecruitmentStage] = useState(null);
  const canViewCommunication = userRole === 'admin' || userRole === 'assistant';

  // Fetch recruitment stage from player profile
  useEffect(() => {
    const token = window.localStorage.getItem('authorization');
    if (!token) return;
    axios
      .get(`${url}/player-auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (res.data.player?.recruitmentStage) {
          setRecruitmentStage(res.data.player.recruitmentStage);
        }
      })
      .catch(() => {});
  }, []);

  // Keep selectedTask panel in sync when Redux items update
  useEffect(() => {
    if (selectedTask) {
      const updated = items.find(i => i._id === selectedTask._id);
      if (updated) setSelectedTask(updated);
    }
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(fetchMyChecklist());
  }, [dispatch]);

  useEffect(() => {
    if (!canViewCommunication && activeTab === 'komunikacja') {
      setActiveTab('tasks');
    }
  }, [activeTab, canViewCommunication]);

  // Auto-open task from ?task= query param (e.g. clicked from notification)
  useEffect(() => {
    if (!items.length) return;
    const params = new URLSearchParams(location.search);
    const taskId = params.get('task');
    if (taskId) {
      const task = items.find(i => i._id === taskId);
      if (task) {
        setSelectedTask(task);
        dispatch(markMyTaskRead(taskId));
      }
    }
  }, [items, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddLink = (taskId) => {
    const linkUrl = (newLinkInputs[taskId] || '').trim();
    if (!linkUrl) return;
    dispatch(addMyLink(taskId, linkUrl));
    setNewLinkInputs(prev => ({ ...prev, [taskId]: '' }));
  };

  const steps = [1];
  if (step2Unlocked) steps.push(2);
  if (step3Unlocked) steps.push(3);

  return (
    <PlayerLayout>
      <PlayerNav />
      <h2 style={{ color: '#1e293b', fontWeight: '700', marginBottom: '8px' }}>
        <i className="fas fa-tasks" style={{ marginRight: '8px', color: '#2e496b' }} />
        Mój Checklist Rekrutacyjny
      </h2>
      <p style={{ color: '#64748b', marginBottom: '16px', fontSize: '14px' }}>
        Śledź postęp swojego procesu rekrutacji do uczelni w USA.
      </p>

      {/* Recruitment stage progress bar */}
      {recruitmentStage && <RecruitmentProgressBar stage={recruitmentStage} />}

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '24px', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
        <button
          type="button"
          onClick={() => setActiveTab('tasks')}
          style={{
            fontSize: '13px', padding: '5px 14px', borderRadius: '4px', cursor: 'pointer',
            border: activeTab === 'tasks' ? '1.5px solid #2e496b' : '1px solid #cbd5e1',
            background: activeTab === 'tasks' ? '#2e496b' : '#f8fafc',
            color: activeTab === 'tasks' ? '#fff' : '#475569',
            fontWeight: activeTab === 'tasks' ? '600' : '400',
          }}
        >
          <i className="fas fa-tasks" style={{ marginRight: '5px' }} />
          Zadania
        </button>
        {canViewCommunication && (
          <button
            type="button"
            onClick={() => setActiveTab('komunikacja')}
            style={{
              fontSize: '13px', padding: '5px 14px', borderRadius: '4px', cursor: 'pointer',
              border: activeTab === 'komunikacja' ? '1.5px solid #2e496b' : '1px solid #cbd5e1',
              background: activeTab === 'komunikacja' ? '#2e496b' : '#f8fafc',
              color: activeTab === 'komunikacja' ? '#fff' : '#475569',
              fontWeight: activeTab === 'komunikacja' ? '600' : '400',
            }}
          >
            <i className="fas fa-comments" style={{ marginRight: '5px' }} />
            Komunikacja
          </button>
        )}
      </div>

      {/* Communication log tab */}
      {canViewCommunication && activeTab === 'komunikacja' && (
        <CommunicationLog items={items} />
      )}

      {/* Status filter (tasks tab only) */}
      {activeTab === 'tasks' && (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '24px', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>Status:</span>
        {FILTER_STATUS_OPTIONS.map(({ val, lbl }) => (
          <button
            key={String(val)}
            type="button"
            onClick={() => setFilterStatus(val)}
            style={{
              fontSize: '12px', padding: '3px 12px', borderRadius: '4px', cursor: 'pointer',
              border: filterStatus === val ? '1.5px solid #2e496b' : '1px solid #cbd5e1',
              background: filterStatus === val ? '#2e496b' : '#f8fafc',
              color: filterStatus === val ? '#fff' : '#475569',
              fontWeight: filterStatus === val ? '600' : '400',
            }}
          >
            {lbl}
          </button>
        ))}
      </div>
      )}

      {activeTab === 'tasks' && loading ? (
        <Loader />
      ) : activeTab === 'tasks' && (
        <>
        {steps.map(step => {
          const allStepItems = items.filter(i => i.step === step);
          const doneCount = allStepItems.filter(i => i.status === 'done').length;
          const progress = allStepItems.length > 0 ? Math.round((doneCount / allStepItems.length) * 100) : 0;
          const stepItems = allStepItems.filter(i => filterStatus === null || i.status === filterStatus);

          return (
            <div key={step} style={{ marginBottom: '32px' }}>
              {/* Step header */}
              <div
                style={{
                  background: '#2e496b',
                  color: '#fff',
                  padding: '10px 16px',
                  borderRadius: '8px 8px 0 0',
                  fontWeight: '600',
                  fontSize: '14px',
                  letterSpacing: '0.05em',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span>Krok {step}: {STEP_TITLES[step]}</span>
                  <span style={{ fontSize: '12px', opacity: 0.85 }}>
                    {doneCount} / {allStepItems.length}
                  </span>
                </div>
                <div
                  style={{
                    background: 'rgba(255,255,255,0.25)',
                    borderRadius: '4px',
                    height: '6px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${progress}%`,
                      height: '100%',
                      background: progress === 100 ? '#22c55e' : '#60a5fa',
                      borderRadius: '4px',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>

              {/* Task rows */}
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  overflow: 'hidden',
                }}
              >
                {stepItems.length === 0 && (
                  <div style={{ padding: '16px', color: '#94a3b8', fontSize: '14px', textAlign: 'center' }}>
                    Brak zadań w tym kroku.
                  </div>
                )}
                {stepItems.map((item, idx) => {
                  const badge = STATUS_BADGE[item.status] || STATUS_BADGE.not_started;
                  const deadlineStr = formatDeadline(item.deadline);

                  return (
                    <div
                      key={item._id}
                      style={{
                        padding: '12px 14px',
                        borderBottom: idx < stepItems.length - 1 ? '1px solid #f1f5f9' : 'none',
                        background: item.status === 'done' ? '#f0fdf4' : '#fff',
                      }}
                    >
                      {/* Row 1: dot + label + deadline + status badge */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <span
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            background:
                              item.status === 'done'
                                ? '#22c55e'
                                : item.status === 'in_progress'
                                ? '#f59e0b'
                                : item.status === 'to_verify'
                                ? '#f97316'
                                : item.status === 'on_hold'
                                ? '#94a3b8'
                                : (item.status === 'waiting' || item.status === 'waiting_athlete' || item.status === 'waiting_university' || item.status === 'waiting_playsport')
                                ? '#60a5fa'
                                : '#cbd5e1',
                            flexShrink: 0,
                          }}
                        />
                        <span
                          style={{
                            flex: 1,
                            fontSize: '14px',
                            color: item.status === 'done' ? '#15803d' : '#334155',
                            textDecoration: item.status === 'done' ? 'line-through' : 'none',
                            minWidth: '140px',
                          }}
                        >
                          {item.label}
                        </span>
                        {deadlineStr && (
                          <span
                            style={{
                              fontSize: '11px',
                              background: '#fef9f0',
                              color: '#92400e',
                              border: '1px solid #fde68a',
                              borderRadius: '4px',
                              padding: '1px 7px',
                              whiteSpace: 'nowrap',
                            }}
                            title="Termin"
                          >
                            <i className="fas fa-calendar-alt" style={{ marginRight: '4px' }} />
                            {deadlineStr}
                          </span>
                        )}
                        <span
                          style={{
                            fontSize: '12px',
                            background: badge.bg,
                            color: badge.color,
                            borderRadius: '4px',
                            padding: '2px 10px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {badge.label}
                        </span>

                        {/* Priority badge (read-only) */}
                        {item.priority && (
                          <span
                            style={{
                              fontSize: '11px',
                              borderRadius: '4px',
                              padding: '1px 8px',
                              whiteSpace: 'nowrap',
                              background: item.priority === 'high' ? '#fee2e2' : item.priority === 'medium' ? '#fef3c7' : '#dbeafe',
                              color: item.priority === 'high' ? '#b91c1c' : item.priority === 'medium' ? '#92400e' : '#1e40af',
                            }}
                          >
                            {item.priority === 'high' ? 'Pilne' : item.priority === 'medium' ? 'Normalny' : 'Niski'}
                          </span>
                        )}

                        {/* TaskOwner (read-only) */}
                        {item.taskOwner && (
                          <span style={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                            {item.taskOwner === 'athlete' ? '👤 Twoje zadanie' : '🏢 Agencja'}
                          </span>
                        )}

                        {/* Requires badges */}
                        {Array.isArray(item.requires) && item.requires.length > 0 && (
                          <span style={{ display: 'inline-flex', gap: '3px' }}>
                            {item.requires.includes('text') && (
                              <span style={{ fontSize: '10px', background: '#fef3c7', color: '#92400e', borderRadius: '4px', padding: '1px 5px' }}>
                                <i className="fas fa-pen" style={{ marginRight: '2px', fontSize: '9px' }} />Tekst
                              </span>
                            )}
                          </span>
                        )}

                        {/* Comment button */}
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTask(item);
                            dispatch(markMyTaskRead(item._id));
                          }}
                          style={{
                            background: 'none',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            color: '#2e496b',
                            cursor: 'pointer',
                            fontSize: '12px',
                            padding: '2px 8px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            position: 'relative',
                          }}
                          title="Komentarze"
                        >
                          <i className="fas fa-comments" />
                          {item.comments && item.comments.filter(c => (c.authorRole === 'admin' || c.authorRole === 'assistant') && !c.readByOtherParty).length > 0 && (
                            <span
                              style={{
                                position: 'absolute',
                                top: '-6px',
                                right: '-6px',
                                background: '#e53e3e',
                                color: '#fff',
                                borderRadius: '50%',
                                width: '16px',
                                height: '16px',
                                fontSize: '9px',
                                fontWeight: '700',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: 1,
                              }}
                            >
                              {item.comments.filter(c => (c.authorRole === 'admin' || c.authorRole === 'assistant') && !c.readByOtherParty).length}
                            </span>
                          )}
                        </button>
                      </div>

                      {/* Files row — only shown when requires includes 'file' */}
                      {(() => {
                        const req = item.requires;
                        const showFiles = Array.isArray(req) && req.includes('file');
                        if (!showFiles) return null;
                        return (
                          <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', color: '#94a3b8', marginRight: '2px' }}>
                              <i className="fas fa-paperclip" />
                            </span>
                            {Array.isArray(item.attachments) && item.attachments.map(att => {
                              const isImage = att.contentType && att.contentType.startsWith('image/');
                              const isPdf = att.contentType === 'application/pdf';
                              const attUrl = getAttachmentDownloadUrl(item._id, att._id);
                              return (
                                <span
                                  key={att._id}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    background: '#f1f5f9',
                                    borderRadius: '4px',
                                    padding: '3px 8px',
                                    fontSize: '12px',
                                  }}
                                >
                                  {isImage && (
                                    <span
                                      style={{ width: '24px', height: '24px', borderRadius: '3px', overflow: 'hidden', flexShrink: 0, cursor: 'pointer' }}
                                      onClick={() => openAttachment(attUrl, att.originalName)}
                                      title="Podgląd"
                                    >
                                      <img
                                        src={attUrl}
                                        alt=""
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={e => { e.target.style.display = 'none'; }}
                                      />
                                    </span>
                                  )}
                                  {isPdf && (
                                    <i className="fas fa-file-pdf" style={{ color: '#dc2626', fontSize: '14px', flexShrink: 0 }} />
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => openAttachment(attUrl, att.originalName)}
                                    style={{ background: 'none', border: 'none', padding: 0, color: '#2e496b', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}
                                    title={att.originalName}
                                  >
                                    {att.originalName}
                                  </button>
                                  {att.description && (
                                    <span style={{ fontSize: '10px', color: '#64748b', fontStyle: 'italic' }} title={att.description}>
                                      ({att.description.length > 20 ? att.description.slice(0, 20) + '...' : att.description})
                                    </span>
                                  )}
                                  {att.createdAt && (
                                    <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                                      {new Date(att.createdAt).toLocaleDateString('pl-PL')}
                                    </span>
                                  )}
                                  {att.uploadedBy && (
                                    <span style={{ fontSize: '10px', color: '#94a3b8' }} title={att.uploadedBy}>
                                      {att.uploadedBy.split('@')[0]}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => dispatch(deleteMyAttachment(item._id, att._id))}
                                    style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: '11px', padding: '0 2px' }}
                                    title="Usuń plik"
                                  >
                                    <i className="fas fa-times" />
                                  </button>
                                </span>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => setUploadModalTaskId(item._id)}
                              style={{
                                background: 'none',
                                border: '1px dashed #cbd5e1',
                                borderRadius: '4px',
                                color: '#64748b',
                                cursor: 'pointer',
                                fontSize: '11px',
                                padding: '2px 10px',
                              }}
                            >
                              + Dodaj plik
                            </button>
                          </div>
                        );
                      })()}

                      {/* Links row — only shown when requires includes 'link' */}
                      {(() => {
                        const req = item.requires;
                        const showLinks = Array.isArray(req) && req.includes('link');
                        if (!showLinks) return null;
                        return (
                          <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', color: '#94a3b8', marginRight: '2px' }}>
                              <i className="fas fa-link" />
                            </span>
                            {Array.isArray(item.links) && item.links.map(lnk => (
                              <span
                                key={lnk._id}
                                style={{
                                  display: 'inline-flex',
                                  maxWidth: '100%',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: '#eff6ff',
                                  borderRadius: '4px',
                                  padding: '2px 8px',
                                  fontSize: '12px',
                                }}
                              >
                                <span style={{ flex: 1, minWidth: 0 }}>
                                  <a
                                    href={lnk.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#1d4ed8', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    title={lnk.url}
                                  >
                                    {displayLinkHost(lnk.url)}
                                  </a>
                                  <ChecklistLinkDate createdAt={lnk.createdAt} />
                                </span>
                                <button
                                  type="button"
                                  onClick={() => dispatch(deleteMyLink(item._id, lnk._id))}
                                  style={{ background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: '11px', padding: '0 2px' }}
                                  title="Usuń link"
                                >
                                  <i className="fas fa-times" />
                                </button>
                              </span>
                            ))}
                            <input
                              type="text"
                              placeholder="Wklej link (YouTube, Drive, Dropbox…)"
                              value={newLinkInputs[item._id] || ''}
                              onChange={e => {
                                const val = e.target.value;
                                setNewLinkInputs(prev => ({ ...prev, [item._id]: val }));
                              }}
                              onKeyDown={e => e.key === 'Enter' && handleAddLink(item._id)}
                              style={{
                                fontSize: '11px',
                                padding: '3px 8px',
                                border: '1px dashed #bfdbfe',
                                borderRadius: '4px',
                                width: '100%', maxWidth: '220px', minWidth: '0',
                                color: '#334155',
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleAddLink(item._id)}
                              style={{
                                background: 'none',
                                border: '1px dashed #bfdbfe',
                                borderRadius: '4px',
                                color: '#1d4ed8',
                                cursor: 'pointer',
                                fontSize: '11px',
                                padding: '2px 10px',
                              }}
                            >
                              + Dodaj link
                            </button>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {(!step2Unlocked || !step3Unlocked) && !loading && (
          <div
            style={{
              marginTop: '16px',
              border: '2px dashed #cbd5e1',
              borderRadius: '10px',
              padding: '32px 24px',
              textAlign: 'center',
              color: '#64748b',
              background: '#f8fafc',
            }}
          >
            <i className="fas fa-lock" style={{ fontSize: '28px', color: '#94a3b8', marginBottom: '12px', display: 'block' }} />
            <div style={{ fontWeight: '600', fontSize: '15px', color: '#475569', marginBottom: '6px' }}>
              {!step2Unlocked && !step3Unlocked
                ? 'Krok 2 i Krok 3 — zablokowane'
                : !step2Unlocked
                ? 'Krok 2 — zablokowany'
                : 'Krok 3 — zablokowany'}
            </div>
            <div style={{ fontSize: '13px', color: '#94a3b8' }}>
              Kolejne etapy zostaną odblokowane po oficjalnej akceptacji oferty z uczelni.
            </div>
          </div>
        )}
        </>
      )}
      {selectedTask && (
        <TaskCommentPanel
          task={selectedTask}
          playerId={null}
          userRole="player"
          onClose={() => {
            setSelectedTask(null);
            if (new URLSearchParams(location.search).has('task')) {
              history.replace(location.pathname);
            }
          }}
        />
      )}
      <FileUploadModal
        show={!!uploadModalTaskId}
        onClose={() => setUploadModalTaskId(null)}
        onUpload={(file, desc) => {
          dispatch(uploadMyAttachment(uploadModalTaskId, file, desc || undefined));
          setUploadModalTaskId(null);
        }}
      />
    </PlayerLayout>
  );
};

export default PlayerChecklist;
